import { Box, Button, Grid, Typography } from '@mui/material';
import { t } from 'i18next';
import { groupBy, noop, orderBy } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { visuallyHidden } from '@mui/utils';
import {
  fetchTotalDataUnderManagement,
  getEvidenceSourcesByWorkspaceId,
  getRefreshDates,
  getWorkspaceSummary,
  getCompletedMappedDataProjectTotals,
} from '../../../api/ReportingManagement';
import {
  getEcaSummary,
  getLoadFiles,
  getDefaultData,
} from '../../../api/EcaManagementService';
import {
  getDeepLinkConfig,
  getWelcomeScreenAlert,
  updateAlertStatusAsViewed,
} from '../../../api/UserManagementService';
import config from '../../../config';
import {
  BUDGET_CONSUMPTON_PERCENTAGE,
  BUDGET_PERCENTAGE_LIMIT,
  DEFAULT_PROJECT_COUNTRY_CODE,
  EciHomePageTabs,
  HOST_URLS,
  INTENT,
  ONLY_DATE_SHOW_FORMAT,
  POPUP_BUTTON_STYLE,
} from '../../../constants/constants';
import navConstants from '../../../constants/navConstants';
import { setModalDropDownSelectedItem } from '../../../redux/slices/invoiceModalSlice';
import {
  continueNCloseDialog,
  setOpenCount,
} from '../../../redux/slices/reportsDialogSlice';
import {
  RefreshDates,
  setPreviousReportTypeTab,
  setProcessingTab,
  setProjectNlpSearch,
  setRefreshDates,
  setReportPageOpenFrom,
  storeProjectNWorkSpaceId,
  storeSelectedDataSet,
} from '../../../redux/slices/reportsSlice';
import { RootState } from '../../../redux/store';
import {
  formatNumberWithComma,
  formatCurrencyAmount,
  getSubtractedDateInUTC,
  getUTCDateTimeForDisplay,
  getHostCountryCode,
  manipulateProjectCountryCode,
} from '../../../utils/utils';
import { EcaSummaryData } from '../../client-details/project-data-table/types';
import AurAlertCarousel from '../../common/aur-alert/AurAlertCarousel';
import AurButton from '../../common/aur-button/AurButton';
import AurCountryFlag from '../../common/aur-country-flag/AurCountryFlag';
import AurDialog from '../../common/aur-dialog/AurDialog';
import AurHighlyConfidential from '../../common/aur-highly-confidential/AurHighlyConfidential';
import AurIcon, { IconColors, IconNames } from '../../common/aur-icon/AurIcon';
import AurLoadingIndicator from '../../common/aur-loading-indicator/AurLoadingIndictor';
import AurMenu from '../../common/aur-menu/AurMenu';
import AurPieChart from '../../common/aur-piechart/AurPieChart';
import AurSvgIcon from '../../common/aur-svg-icon/AurSvgIcon';
import { ReportScreenTabTypes } from '../reports/ProjectOverview';
import DatasetAlertsDialog, {
  DatasetAlert,
  DatasetAlertsDialogRef,
} from './DatasetAlertsDialog';
import { Project, ProjectStatus } from './model';
import WorkspaceOverView from './WorkspaceOverView';
import ConsilioWorkflowMetrics from './project-metrics/ConsilioWorkflowMetrics';
import NonConsilioWorkflowMetrics from './project-metrics/NonConsilioWorkflowMetrics';
import ProjectProfileDialog from './ProjectProfileDialog';
import ProjectProfileEditableDetails from './ProjectProfileEditableDetails';
import ProjectProfileViewDetails from './ProjectProfileViewDetails';
import { updateProjectByProjectId } from '../../../api/ClientManagementService';
import { ProjectFields } from '../../../types/project-type/projectDetailsType';
import AurDonutchart from '../../common/aur-donut-chart/AurDonutChart';
import { useToast } from '../../../contexts/toastContext/ToastContext';
import {
  setBudgetAlertCount,
  setShowBudgetTrackerModal,
} from '../../../redux/slices/budgetTrackerSlice';
import { getBudgetTrackerDetails } from '../../../api/SelectiveSelfManagementService';
import { IBudgetTrackerResponse } from '../../budget-tracker/model';
import {
  setSelectedProjectId,
  setStartScroll,
} from '../../../redux/slices/projectOverviewSlice';
import {
  clearUploadEvidenceSource,
  setSelectedProjectForEvidenceSource,
} from '../../../redux/slices/uploadEvidenceSourceSlice';
import { AurLightTooltip } from '../../common/aur-light-tooltip/aur-light-tooltip';
import { setReportSrc } from '../../../redux/slices/analyticsSlice';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../constants/analyticsConstants';
import {
  PAGE_NAME,
  usePermissionsByPage,
} from '../../../hooks/permissions/usePermissionsByPage';
import usePermissionsByProjectId from '../../../hooks/permissions/usePermissionsByProjectId';
import {
  FeatureSettingEnum,
  isFeatureSettingEnabled,
} from '../../../redux/slices/featureSettingsSlice';
import { selectUser, UserInfoType } from '../../../redux/slices/userSlice';
import AurPopup from '../../common/aur-popup/AurPopup';
import { useAnalytics } from '../../../hooks/useAnalytics';

interface IProps {
  projectDetails: Project;
  isProjectClosed: boolean;
  updateAlertCount?: () => void;
}

interface QuickLinkItem {
  label: string;
  onClick: () => void;
  index: number;
}

export interface ReportMetrics {
  mediaAndEvidenceSets: number;
  datasetsProcessing: number;
  datasetsProcessed: number;
  totalDataUnderManagement: number;
  hostingUsers: number;
  hostingUserSummaryDate: string;
  productionsCompleted: number;
  totalDocumentsCount: number;
  exportedDocumentCount: number;
  totalVolumesMapped: number;
}

const ProjectOverView: React.FC<IProps> = (props: IProps) => {
  const navigate = useNavigate();
  const { trackGAEvent } = useAnalytics();
  const { projectDetails, isProjectClosed, updateAlertCount } = props;
  const clientId = useSelector(
    (state: RootState) => state.selectedClient
  ).clientId;
  const metricsUpdatedOn: string | null | undefined = useSelector(
    (state: RootState) => state.reports.overviewDataset
  )?.metricsUpdatedOn;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [workspacesExpanded, setWorkspacesExpanded] = useState(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [canViewEca, setCanViewEca] = useState<boolean>(false);
  const [
    totalDataManagementdataDialogIsOpen,
    setTotalDataManagementdataDialogIsOpen,
  ] = useState(false);
  const [totalDataManagementChartData, setTotalDataManagementChartData] =
    useState<{ value: number; label: string }[]>([]);
  const open = Boolean(anchorEl);
  const [formattedDate, setFormattedDate] = useState<string>('');
  const [alerts, setAlerts] = useState<any>([]);
  const datasetAlertsDialogRef = useRef<DatasetAlertsDialogRef>(null);
  const [reportMetrics, setReportMetrics] = useState<ReportMetrics>();
  const [budgetReport, setBudgetReport] = useState<boolean>(false);

  const [budgetDetails, setBudgetDetails] = useState<
    IBudgetTrackerResponse['data'] | null
  >(null);
  const { isPermissionLoading, getPermissionsByClientIdProjectId } =
    usePermissionsByProjectId(projectDetails.projectId);
  const userData: UserInfoType | null = useSelector(selectUser);
  const [showLaunchpointPopup, setShowLaunchpointPopup] = useState(false);
  const isGlobalScalingEnabled = useSelector((state: RootState) =>
    isFeatureSettingEnabled(state, FeatureSettingEnum.GlobalScalingEnabled)
  );
  const [projectCountryCode, setProjectCountryCode] = useState<string>('');
  const [showUploadEvidencePopup, setShowUploadEvidencePopup] = useState(false);

  const {
    canViewConciergeReports,
    canViewInvoiceReports,
    canViewProjectTracker,
    canUploadEvidence,
    canManageEvidence,
    canInitiateProcessing,
    canReportBudgetConsumption,
    canAccessECI,
    canEditProjectProfile,
    canViewCompletedMappedDataReports,
  } = usePermissionsByPage(
    PAGE_NAME.PROJECT_OVERVIEW,
    projectDetails.projectId
  );

  /*
    Handler for closing a menu or popover
    */

  const handleClose = () => {
    setAnchorEl(null);
  };

  const dispatch = useDispatch();

  const navigateToReport = (url: string, states?: any) => {
    dispatch(setReportSrc(GA_REGIONS.BODY));
    dispatch(setReportPageOpenFrom('/welcome-single-client'));
    if (states) {
      navigate(url, states);
    } else {
      navigate(url);
    }
  };

  const handleCardClick = useCallback(
    (tabType: ReportScreenTabTypes) => {
      dispatch(
        storeProjectNWorkSpaceId({
          projectId: projectDetails.projectId,
          workspaceId: projectDetails.workspaces[0]?.workspaceId,
          projectName: projectDetails.projectName,
          workspaceName: projectDetails.workspaces[0]?.name,
          projectCode: projectDetails.projectCode,
          projectAlphaCode: projectDetails.projectAlphaCode,
        })
      );
      dispatch(setPreviousReportTypeTab(undefined));
      updateProjectWorkspaceState(projectDetails.projectId ?? '', false);
      dispatch(setReportPageOpenFrom('/welcome-single-client'));
      navigateToReport(`/reports?tab=${tabType}`);
    },
    [dispatch, projectDetails]
  );

  const fetchDataUnderManagement = async () => {
    const projectAlphaCode = projectDetails?.projectAlphaCode;
    if (!projectAlphaCode) {
      return;
    }

    try {
      setIsLoading(true);
      const response = await fetchTotalDataUnderManagement(projectAlphaCode);
      const chartData = response?.data.data.map(
        (item: { value: number; label: string }, _index: number) => {
          if (item.label.includes('Relativity'))
            return {
              value: item.value,
              label: item.label,
              onClick: () => {
                handleCardClick(ReportScreenTabTypes.HOSTING_VOLUME);
              },
            };
          else return { value: item.value, label: item.label };
        }
      );

      setTotalDataManagementChartData(chartData);
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  const onClickMenuButton = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const openInNewTab = (url: string) => () =>
    window.open(url, t('common.targetBlank'));

  const fetchLoadFiles = async () => {
    try {
      const paramString = `clientId=${clientId}&projectId=${projectDetails.projectId}`;
      const response = await getLoadFiles(paramString);
      const res = response.data;
      const data = res?.data || [];

      const latestItem = data.length
        ? data.reduce((latest: any, current: any) => {
          const latestDate = new Date(latest.lastLoadDateTime);
          const currentDate = new Date(current.lastLoadDateTime);
          return currentDate > latestDate ? current : latest;
        })
        : undefined;

      const checkDate = latestItem ? latestItem.lastLoadDateTime : undefined;
      setFormattedDate(
        checkDate
          ? getUTCDateTimeForDisplay(checkDate, ONLY_DATE_SHOW_FORMAT)
          : ''
      );
      // eslint-disable-next-line no-empty
    } catch (error) {
      console.warn(error);
    }
  };

  const [quickLinkList, setQuickLinkList] = useState<QuickLinkItem[]>([]);

  const getTotalVolumesMapped = async () => {
    if (!canViewCompletedMappedDataReports) {
      return 0;
    }

    try {
      const query = {
        clientId: clientId,
        projectId: projectDetails.projectId,
        projectAlphaCode: projectDetails.projectAlphaCode,
      };
      const response = await getCompletedMappedDataProjectTotals(query);
      const data = response.data.data;
      return Number(data.totalVolumesMapped);
    } catch (error) {
      console.error('Failed to fetch Total Volumes Mapped.', error);
      return 0;
    }
  };

  const fetchEcaSummary = async () => {
    setIsLoading(true);
    try {
      const paramString = `clientId=${clientId}&projectid=${projectDetails.projectId}`;
      const response = await getEcaSummary(paramString);
      const data: EcaSummaryData = await response.data.data;
      const totalVolumesMapped = await getTotalVolumesMapped();

      setReportMetrics({
        mediaAndEvidenceSets: data?.numberOfMediaAndEvidenceSets || 0,
        datasetsProcessing: data?.numberofDataSetsUnderProcessing || 0,
        datasetsProcessed: data?.numberofDataSetsProcessed || 0,
        totalDataUnderManagement: data?.dataVolumeUnderManagementInGB || 0,
        totalDocumentsCount: data?.totalDocumentsCount || 0,
        exportedDocumentCount: data?.exportedDocumentCount || 0,
        hostingUsers: 0,
        hostingUserSummaryDate: '',
        productionsCompleted: 0,
        totalVolumesMapped: totalVolumesMapped,
      });

      setIsLoading(false);
    } catch (_) {
      setIsLoading(false);
    }
  };

  const modalDropDownSelectedItem = useMemo(() => {
    return {
      id: projectDetails.projectId,
      name: projectDetails.projectName,
      code: projectDetails.projectCode,
      alphaCode: projectDetails.projectAlphaCode,
      domainCountryCode: projectDetails.domainCountryCode,
    };
  }, [
    projectDetails.domainCountryCode,
    projectDetails.projectAlphaCode,
    projectDetails.projectCode,
    projectDetails.projectId,
    projectDetails.projectName,
  ]);

  const isDifferentDomain = useMemo(() => {
    const hostCountryCode = getHostCountryCode();
    const projectCountryCode = manipulateProjectCountryCode(
      hostCountryCode,
      projectDetails.domainCountryCode
        ? projectDetails.domainCountryCode
        : DEFAULT_PROJECT_COUNTRY_CODE
    );
    setProjectCountryCode(projectCountryCode);
    return hostCountryCode.toLowerCase() !== projectCountryCode.toLowerCase();
  }, [projectDetails.projectId, clientId]);

  const callGAEvent = (name: string, linkName: string, type: string) => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name,
        type,
        region: GA_REGIONS.BODY,
        action: `selected - ${linkName}`,
      },
      subPage: {
        subpage_name: t('gaTracking.projectViewTab.projectView'),
        subpage_type: GA_SUBPAGE_TYPE.ACCORDION,
      },
      hasProjectData: true,
    });
  };

  useEffect(() => {
    const dynamicLinks: QuickLinkItem[] = [];

    if (canViewConciergeReports) {
      dynamicLinks.push({
        label: t('welcomeScreenSingleClient.conciergeReports'),
        onClick: () => {
          callGAEvent(
            t('gaTracking.projectViewTab.quickLinks.customReports'),
            t('gaTracking.projectViewTab.quickLinks.customReports'),
            GA_CLICK_TYPES.DROPDOWN
          );
          dispatch(setModalDropDownSelectedItem(modalDropDownSelectedItem));
          navigate(navConstants.reports.conciergeReports);
        },
        index: 1,
      });
    }

    if (canViewInvoiceReports) {
      dynamicLinks.push({
        label: t('welcomeScreenSingleClient.invoiceReports'),
        onClick: () => {
          callGAEvent(
            t('gaTracking.projectViewTab.quickLinks.invoiceReports'),
            t('gaTracking.projectViewTab.quickLinks.invoiceReports'),
            GA_CLICK_TYPES.DROPDOWN
          );
          dispatch(setModalDropDownSelectedItem(modalDropDownSelectedItem));
          navigateToReport(navConstants.reports.viewInvoices);
        },
        index: 2,
      });
    }

    if (canViewProjectTracker) {
      dynamicLinks.push({
        label: t('welcomeScreenSingleClient.projectTracker'),
        onClick: () => {
          callGAEvent(
            t('gaTracking.projectViewTab.quickLinks.projectTracker'),
            t('gaTracking.projectViewTab.quickLinks.projectTracker'),
            GA_CLICK_TYPES.DROPDOWN
          );
          openInNewTab(config.PROJECT_TRACKER_URL)();
        },
        index: 3,
      });
    }
    if (canUploadEvidence) {
      dynamicLinks.push({
        label: t('welcomeScreenSingleClient.uploadNewEvidenceSource'),
        onClick: () => {
          callGAEvent(
            t('gaTracking.projectViewTab.quickLinks.uploadNewEvidenceSource'),
            t('gaTracking.projectViewTab.quickLinks.uploadNewEvidenceSource'),
            GA_CLICK_TYPES.DROPDOWN
          );
          if (isDifferentDomain) {
            setShowUploadEvidencePopup(true);
          } else {
            dispatch(clearUploadEvidenceSource());
            dispatch(
              setSelectedProjectForEvidenceSource({
                projectAlphaCode: projectDetails.projectAlphaCode,
                projectId: projectDetails.projectId,
                projectName: projectDetails.projectName,
                isNonConsilioWorkflow: projectDetails.isNonConsilioWorkflow,
              })
            );
            navigate('/upload-evidence/new-evidence');
          }
        },
        index: 4,
      });
    }

    if (canManageEvidence) {
      dynamicLinks.push({
        label: t('welcomeScreenSingleClient.manageProjectEvidence'),
        onClick: () => {
          callGAEvent(
            t('gaTracking.projectViewTab.quickLinks.manageProjectEvidence'),
            t('gaTracking.projectViewTab.quickLinks.manageProjectEvidence'),
            GA_CLICK_TYPES.DROPDOWN
          );
          if (isDifferentDomain) {
            setShowUploadEvidencePopup(true);
          } else {
            const {
              projectAlphaCode,
              projectId,
              projectName,
              workspaces,
              projectCode,
              isNonConsilioWorkflow,
            } = projectDetails;
            dispatch(
              setSelectedProjectForEvidenceSource({
                projectAlphaCode: projectAlphaCode,
                projectId: projectId,
                projectName: projectName,
                isNonConsilioWorkflow: isNonConsilioWorkflow,
              })
            );
            dispatch(
              storeProjectNWorkSpaceId({
                projectId: projectId,
                workspaceId: workspaces?.[0]?.workspaceId ?? '',
                projectName: projectName,
                workspaceName: workspaces?.[0]?.name ?? '',
                projectCode: projectCode,
                projectAlphaCode: projectAlphaCode,
              })
            );
            navigate('/manage-evidence');
          }
        },
        index: 5,
      });
    }

    const getSorting = (a: QuickLinkItem, b: QuickLinkItem) => {
      if (a.index > b.index) {
        return 1;
      } else if (b.index > a.index) {
        return -1;
      }
      return 0;
    };
    setQuickLinkList(dynamicLinks.slice().sort((a, b) => getSorting(a, b)));
  }, [
    canViewConciergeReports,
    canViewInvoiceReports,
    canViewProjectTracker,
    canUploadEvidence,
    canManageEvidence,
    canInitiateProcessing,
  ]);

  useEffect(() => {
    getReportRefreshDate();
  }, []);

  // api for budget tracker details
  const fetchBudgetTrackerDetails = async () => {
    setIsLoading(true);
    dispatch(setModalDropDownSelectedItem(modalDropDownSelectedItem));
    try {
      const response = await getBudgetTrackerDetails(
        clientId,
        projectDetails.projectId
      );
      const budgetTrackerData = response?.data?.data;
      setBudgetDetails(budgetTrackerData);
    } catch (error) {
      console.error('Error fetching overlays:', error);
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    if (budgetReport) fetchBudgetTrackerDetails();
  }, [budgetReport]);

  useEffect(() => {
    if (isPermissionLoading || canAccessECI === undefined) return;
    const hasPermission = projectDetails.ecaEnabled && canAccessECI;

    if (hasPermission) {
      fetchEcaSummary();
      fetchDataUnderManagement();
      fetchLoadFiles();
    } else {
      fetchDataUnderManagement();
      fetchNonEcaSummary();
    }

    if (hasPermission) {
      setCanViewEca(true);
    }
  }, [
    clientId,
    projectDetails.projectId,
    canAccessECI,
    projectDetails.ecaEnabled,
    isPermissionLoading,
  ]);

  const sortedGroupedAlerts = useMemo(() => {
    const cutoff = getSubtractedDateInUTC(1).toDate();

    const recentAlerts = alerts.filter(
      (alert: any) =>
        new Date(`${alert.triggeredOn}Z`) >= cutoff &&
        alert.alertLevel.toLowerCase() === 'project'
    );
    const groupableTypes = [
      'DatasetCompletedProcessing',
      'DataHasProcessingExceptions',
    ];
    const grouped = Object.entries(
      groupBy(
        recentAlerts.filter((alert: any) =>
          groupableTypes.includes(alert.alertType)
        ),
        'alertType'
      )
    ).map(([key, alerts]) => ({
      alertType: key,
      data: orderBy(alerts, ['triggeredOn'], ['desc']),
      projectName: projectDetails.projectName,
    }));

    const ungrouped = recentAlerts
      .filter((alert: any) => !groupableTypes.includes(alert.alertType))
      .map((alert: any) => ({
        alertType: alert.alertType,
        data: [alert],
        projectName: projectDetails.projectName,
      }));
    const combined = [...grouped, ...ungrouped];

    // Sort final result by most recent triggeredOn in each group
    const sortedGrouped = orderBy(
      combined,
      (group) => group.data[0]?.triggeredOn,
      ['desc']
    );

    return sortedGrouped.map((group) => {
      return {
        ...group,
        ...group.data[0],
      };
    });
  }, [alerts, projectDetails.projectName]);

  const workspaceAlerts = useCallback(
    (workspaceId: string) => {
      const workspaceAlertArray = alerts.filter(
        (alert: any) =>
          alert.alertLevel.toLowerCase() !== 'project' &&
          alert.workspaceId.toLowerCase() === workspaceId.toLowerCase()
      );

      const alertTypeArray = workspaceAlertArray.map(
        (alert: any) => alert?.alertType
      );
      const alertTypes = [...new Set(alertTypeArray)];

      const grouped = Object.entries(
        groupBy(
          workspaceAlertArray.filter((alert: any) =>
            alertTypes.includes(alert?.alertType)
          ),
          'alertType'
        )
      ).map(([key, wsAlerts]) => ({
        alertType: key,
        data: orderBy(wsAlerts, ['triggeredOn'], ['desc']),
      }));

      const sortedGrouped = orderBy(
        grouped,
        (group) => group.data[0]?.triggeredOn,
        ['desc']
      );

      return sortedGrouped.map((group) => ({
        ...group,
        ...group.data[0],
      }));
    },
    [alerts]
  );

  const fetchAlerts = useCallback(async () => {
    try {
      const response = await getWelcomeScreenAlert(
        clientId,
        projectDetails.projectId as string,
        null
        // '0ded8068-d8d5-41da-8e2b-65d44b998ef8'
        // '4d421d8f-5eaa-4939-88e1-d1aa48cd1528'
      );

      const filterData = response?.data?.data?.map((item: any) => {
        if (Object.prototype.hasOwnProperty.call(item, 'dataSets')) {
          const { dataSets, ...rest } = item;
          const filterDataset = dataSets.filter((item: string) => item != 'NA');
          return {
            datasets: filterDataset,
            workspaceName: '',
            ...rest,
          };
        }
        return item;
      });
      setAlerts(filterData);
    } catch (err: any) {
      console.error('', err.message);
    }
  }, [clientId, projectDetails.projectId, projectDetails?.workspaces]);

  useEffect(() => {
    if (clientId && projectDetails.projectId) {
      fetchAlerts();
    }
  }, [clientId, fetchAlerts, projectDetails.projectId]);

  const markAlertAsViewed = (userAlertId: number) => {
    updateAlertStatusAsViewed(userAlertId)
      .then(() => {
        fetchAlerts();
        updateAlertCount?.();
      })
      .catch(noop);
  };

  const reportTypeSelection = (tabName: string) => {
    const nameMapping: { [key: string]: ReportScreenTabTypes } = {
      hostingVolume: ReportScreenTabTypes.HOSTING_VOLUME,
      HighLevelProductionMetrics: ReportScreenTabTypes.PRODUCTION_METRIC,
      intakeAuditReport: ReportScreenTabTypes.INTAKE_AUDIT_REPORT,
      ProcessingExceptions: ReportScreenTabTypes.PROCESSED_DATASET,
      ProcessingUserSummary: ReportScreenTabTypes.PROCESSED_DATASET,
      HostingUserSummary: ReportScreenTabTypes.HOSTING_USERS,
      completedMappedData: ReportScreenTabTypes.COMPLETED_MAPPED_DATA_REPORT,
    };

    return nameMapping[tabName] || undefined;
  };

  const navigationButtonHandler = async (
    alertType: string,
    dataSetEvidence: string
  ) => {
    if (alertType === t('welcomeScreenSingleClient.Invoice')) {
      dispatch(setModalDropDownSelectedItem(modalDropDownSelectedItem));
      navigateToReport(navConstants.reports.viewInvoices);
    } else if (alertType === t('welcomeScreenSingleClient.ConceirgeReport')) {
      dispatch(setModalDropDownSelectedItem(modalDropDownSelectedItem));
      navigateToReport(navConstants.reports.conciergeReports, {
        state: { clientId, projectId: projectDetails.projectId },
      });
    } else if (
      alertType === t('welcomeScreenSingleClient.processingExceptions') ||
      alertType === t('welcomeScreenSingleClient.ProcessingUserSummary')
    ) {
      if (alertType === t('welcomeScreenSingleClient.processingExceptions')) {
        dispatch(setProcessingTab(1));
      } else {
        dispatch(setProcessingTab(0));
      }
      try {
        const queryParam = {
          clientId: clientId,
          projectId: projectDetails.projectId,
          pageSize: '10',
          pageNumber: '1',
          SearchTerm: dataSetEvidence,
        };

        const response = await getEvidenceSourcesByWorkspaceId(
          'workspaceId',
          new URLSearchParams(queryParam).toString()
        );
        dispatch(
          storeSelectedDataSet({
            projectId: projectDetails.projectId,
            workspaceId: 'workspaceId',
            projectName: projectDetails.projectName ?? '',
            projectAlphaCode: projectDetails.projectAlphaCode,
            workspaceName: '',
            workspaceDataSetName: response.data.data[0].dataset ?? '',
            worksapceDataSetMediaId: response.data.data[0].mediaId ?? '',
          })
        );
        dispatch(continueNCloseDialog());
        dispatch(setOpenCount(2));
      } catch (e) {
        console.error(e);
      } finally {
        handleCardClick(reportTypeSelection(alertType));
      }
    } else {
      handleCardClick(reportTypeSelection(alertType));
    }
  };

  const onPressContinueFromAlertsDialog = (alert: DatasetAlert) => {
    navigationButtonHandler(
      alert?.reportType ?? '',
      alert?.datasets?.[0] ?? ''
    );
  };

  const getReportRefreshDate = async () => {
    try {
      const response = await getRefreshDates();
      if (response?.status === 200) {
        const refreshDateObject: RefreshDates = {};
        response?.data?.data.map(
          (item: { name: string; lastRefreshedDate: string }) => {
            refreshDateObject[item.name as keyof RefreshDates] =
              item.lastRefreshedDate;
          }
        );
        dispatch(setRefreshDates(refreshDateObject));
      }
    } catch (error) {
      console.error(error);
    }
  };

  const launchOtherDomainECI = (
    projectCountryCode: string,
    encodedPayload: string
  ) => {
    if (HOST_URLS.DEV.includes(location.host)) {
      window.open(
        `${HOST_URLS.QA}/login?redirectData=${encodedPayload}`,
        '_blank'
      ); //Added this condition for lower environment testing
    } else if (HOST_URLS.UAT1.includes(location.host)) {
      window.open(
        `${HOST_URLS.UAT2}/login?redirectData=${encodedPayload}`,
        '_blank'
      ); //Added this condition for lower environment testing
    } else {
      getDomainConfig(projectCountryCode, encodedPayload);
    }
  };

  const getDomainConfig = async (countryCode: string, payload: string) => {
    try {
      const response = await getDeepLinkConfig();
      const navigationUrl = response?.data?.data.find(
        (config: any) => config.domainContryCode === countryCode
      )?.redirectionUrl;
      navigationUrl
        ? window.open(
          `${navigationUrl}/login?redirectData=${payload}`,
          '_blank'
        )
        : window.open(
          `${navigationUrl}/login?redirectData=${payload}`,
          '_blank'
        );
    } catch {
      setShowLaunchpointPopup(true);
    }
  };

  const launchECI = async () => {
    const paramString = `?clientId=${clientId}&projectId=${projectDetails.projectId}`;
    try {
      await getDefaultData(paramString);
    } catch (error) {
      console.log(error);
    }
  };

  const loadEcaPage = () => {
    const payload = {
      clientId,
      email: userData?.email,
      intent: INTENT.LAUNCH_ECI,
      projectId: projectDetails.projectId,
    };
    const encodedPayload = btoa(JSON.stringify(payload));

    if (isDifferentDomain) {
      isGlobalScalingEnabled
        ? launchOtherDomainECI(projectCountryCode, encodedPayload)
        : setShowLaunchpointPopup(true);
    } else {
      updateProjectWorkspaceState(projectDetails.projectId ?? '', false);
      launchECI();
      getPermissionsByClientIdProjectId();
      dispatch(
        storeProjectNWorkSpaceId({
          projectId: projectDetails.projectId,
          workspaceId: '',
          projectName: projectDetails.projectName,
          workspaceName: '',
          projectCode: projectDetails?.projectCode,
          projectAlphaCode: projectDetails?.projectAlphaCode,
        })
      );
      dispatch(setProjectNlpSearch(projectDetails.nlpSearchEnabled || false));
      navigate(`/eci-home-page?tab=${EciHomePageTabs.ECI_OVERVIEW}`);
    }
  };

  const loadBudgetDetails = () => {
    dispatch(setModalDropDownSelectedItem(modalDropDownSelectedItem));
    // DEV NOTE : To make below dispatch wait for 1st dispatch to complete
    setTimeout(() => {
      dispatch(setShowBudgetTrackerModal(true));
      dispatch(setBudgetAlertCount(0));
    });
  };

  const updateProjectWorkspaceState = (
    projectId: any,
    scrollState: boolean
  ) => {
    dispatch(setSelectedProjectId(projectId));
    dispatch(setStartScroll(scrollState));
  };

  const setReportProject = () => {
    updateProjectWorkspaceState(projectDetails.projectId ?? '', false);

    // Store information about the currently selected item for the reports page
    dispatch(
      storeSelectedDataSet({
        projectId: projectDetails.projectId ?? '',
        projectName: projectDetails.projectName ?? '',
        projectCode: projectDetails.projectCode ?? '',
        projectAlphaCode: projectDetails.projectAlphaCode,
        workspaceId: projectDetails.workspaces[0]?.workspaceId,
        workspaceName: projectDetails.workspaces[0]?.name,
        workspaceDataSetName: '',
        worksapceDataSetMediaId: '',
      })
    );
  };

  const loadIntakeAuditReport = () => {
    callGAEvent(
      t('gaTracking.projectViewTab.dataDeliveries'),
      t('gaTracking.projectViewTab.dataDeliveries'),
      GA_CLICK_TYPES.ACCORDION
    );
    setReportProject();
    navigateToReport(
      `/reports?tab=${ReportScreenTabTypes.INTAKE_AUDIT_REPORT}`
    );
  };

  const loadProcessingInProgressReport = () => {
    callGAEvent(
      t('gaTracking.projectViewTab.dataProcessing'),
      t('gaTracking.projectViewTab.dataProcessing'),
      GA_CLICK_TYPES.ACCORDION
    );
    setReportProject();
    navigateToReport(
      `/reports?tab=${ReportScreenTabTypes.DATA_SET_PROCESSING}`
    );
  };

  const loadSummaryAndExceptionsReport = () => {
    callGAEvent(
      t('gaTracking.projectViewTab.processedDatasets'),
      t('gaTracking.projectViewTab.processedDatasets'),
      GA_CLICK_TYPES.ACCORDION
    );
    setReportProject();
    navigateToReport(`/reports?tab=${ReportScreenTabTypes.PROCESSED_DATASET}`);
  };

  const navigateToCompletedMappedDataReport = () => {
    callGAEvent(
      t('gaTracking.projectViewTab.totalVolumesMapped'),
      t('gaTracking.projectViewTab.totalVolumesMapped'),
      GA_CLICK_TYPES.ACCORDION
    );
    dispatch(setReportSrc(GA_REGIONS.BODY));
    dispatch(setReportPageOpenFrom('/welcome-single-client'));
    navigate('/reports?tab=completed-mapped-data');
    dispatch(
      storeProjectNWorkSpaceId({
        projectId: projectDetails.projectId,
        workspaceId: projectDetails.workspaces[0]?.workspaceId,
        projectName: projectDetails.projectName,
        workspaceName: projectDetails.workspaces[0]?.name,
        projectCode: projectDetails.projectCode,
        projectAlphaCode: projectDetails.projectAlphaCode,
      })
    );
  };

  const fetchNonEcaSummary = async () => {
    const defaultWorkspaceId = projectDetails.workspaces.find(
      (workspace) => projectDetails.projectAlphaCode === workspace.workspaceCode
    )?.workspaceId;

    if (!defaultWorkspaceId) {
      return;
    }

    try {
      setIsLoading(true);
      const paramString = `clientId=${clientId}&projectId=${projectDetails.projectId}&workspaceId=${defaultWorkspaceId}`;
      const response = await getWorkspaceSummary(paramString);
      const data = await response.data.data;
      const totalVolumesMapped = await getTotalVolumesMapped();

      setReportMetrics({
        mediaAndEvidenceSets:
          getWorkspaceSummaryData(data.summary, 'IntakeAudit')
            ?.mediaEvidenceSets || 0,
        datasetsProcessing:
          getWorkspaceSummaryData(data.summary, 'ProcessingInProgress')
            ?.datasetsInProcessing || 0,
        datasetsProcessed:
          getWorkspaceSummaryData(data.summary, 'ProcessingSummary')
            ?.datasetsProcessed || 0,
        totalDataUnderManagement:
          getWorkspaceSummaryData(data.summary, 'WorkspaceSummary')
            ?.totalProjectFileSizeGB || 0,
        hostingUsers:
          getWorkspaceSummaryData(data.summary, 'HostingUserSummary')
            ?.hostingUsers || 0,
        productionsCompleted:
          getWorkspaceSummaryData(data.summary, 'ProductionMetrics')
            ?.productionsCompleted || 0,
        hostingUserSummaryDate: data.hostingUserSummaryDate || '',
        totalDocumentsCount: 0,
        exportedDocumentCount: 0,
        totalVolumesMapped: totalVolumesMapped,
      });

      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.error('Error fetching summary:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getWorkspaceSummaryData = (data: any[], reportType: string): any => {
    return data.find((report: any) => report.reportType === reportType);
  };

  const [projectDescription, setProjectDescription] = useState(
    props.projectDetails.projectDescription ?? ''
  );
  const [overseeingAttorneys, setOverseeingAttorneys] = useState(
    props.projectDetails.overseeingAttorneys ?? ''
  );
  const [entityName, setEntityName] = useState(
    props.projectDetails.entityName ?? ''
  );
  const [clientUniqueProjectId, setClientUniqueProjectId] = useState(
    props.projectDetails.clientUniqueProjectId ?? ''
  );
  const [overseeingAttorneysEmail, setOverseeingAttorneysEmail] = useState(
    props.projectDetails.overseeingAttorneysEmail ?? ''
  );
  const [additionalContactInfo, setAdditionalContactInfo] = useState(
    props.projectDetails.attorneysAdditionalContactInfo ?? ''
  );
  const [internalProjectName, setInternalProjectName] = useState(
    props.projectDetails.internalProjectName ?? ''
  );

  const [openProfileDialog, setOpenProfileDialog] = useState(false);
  const [onProjectEdit, setOnProjectEdit] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setBudgetReport(
      Boolean(
        projectDetails?.budgetConsumptionEnabled && canReportBudgetConsumption
      )
    );
  }, [
    clientId,
    projectDetails.projectId,
    canReportBudgetConsumption,
    projectDetails?.budgetConsumptionEnabled,
  ]);

  useEffect(() => {
    if (openProfileDialog) {
      trackGAEvent({
        eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
        subPage: {
          subpage_name: t('gaTracking.projectViewTab.productProfile'),
          subpage_type: GA_SUBPAGE_TYPE.MODAL,
        },
        hasProjectData: true,
      });
    }
  }, [openProfileDialog]);

  const { showToast } = useToast();

  const handleSave = async () => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.global.save'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.MODAL,
        action: `${t('gaTracking.global.save')} - ${t(
          'gaTracking.projectViewTab.editProjectProfile'
        )}`,
      },
      subPage: {
        subpage_name: t('gaTracking.projectViewTab.editProjectProfile'),
        subpage_type: GA_SUBPAGE_TYPE.MODAL,
      },
      hasProjectData: true,
    });
    const getProjectFields = (): ProjectFields => ({
      description: projectDescription,
      attorneysName: overseeingAttorneys,
      attorneysEmail: overseeingAttorneysEmail,
      attorneysAdditionalContactInfo: additionalContactInfo,
      entityName: entityName,
      internalProjectName: internalProjectName,
      clientUniqueProjectId: clientUniqueProjectId,
      projectId: props.projectDetails.projectId,
      clientId,
    });

    try {
      if (!hasError) {
        await updateProjectByProjectId(
          props.projectDetails.projectId,
          getProjectFields()
        );
        setOnProjectEdit(false);
        showToast(
          t('clientDetailsPage.projectDrawer.saveSuccessToastMsg'),
          'success',
          undefined,
          'center'
        );
      }
    } catch (error) {
      showToast(
        t('clientDetailsPage.projectDrawer.saveErrorToastMsg'),
        'error',
        undefined,
        'center'
      );
    }
  };

  const handleCancel = () => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.global.cancel'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.MODAL,
        action: `${t('gaTracking.global.cancel')} - ${t(
          'gaTracking.projectViewTab.editProjectProfile'
        )}`,
      },
      subPage: {
        subpage_name: t('gaTracking.projectViewTab.editProjectProfile'),
        subpage_type: GA_SUBPAGE_TYPE.MODAL,
      },
      hasProjectData: true,
    });
    setProjectDescription(props.projectDetails.projectDescription ?? '');
    setOverseeingAttorneys(props.projectDetails.overseeingAttorneys ?? '');
    setEntityName(props.projectDetails.entityName ?? '');
    setClientUniqueProjectId(props.projectDetails.clientUniqueProjectId ?? '');
    setOverseeingAttorneysEmail(
      props.projectDetails.overseeingAttorneysEmail ?? ''
    );
    setAdditionalContactInfo(
      props.projectDetails.attorneysAdditionalContactInfo ?? ''
    );
    setInternalProjectName(props.projectDetails.internalProjectName ?? '');

    setOnProjectEdit(false);
  };

  const editActions = (
    <>
      <AurButton
        classNames={`typo-text project-dialog-btn`}
        type="exit"
        size="Medium"
        label={t('label.cancel')}
        onClick={handleCancel}
      />

      <AurButton
        classNames={`typo-text project-dialog-btn`}
        type="continue"
        size="Medium"
        label={t('label.save')}
        onClick={handleSave}
        disabled={hasError}
      />
    </>
  );

  const viewActions = (
    <>
      {canEditProjectProfile && (
        <AurButton
          classNames={`typo-text project-dialog-btn`}
          type="continue"
          size="Medium"
          label={t('label.editDetails')}
          onClick={() => {
            trackGAEvent({
              eventName: GA_EVENT_NAMES.CLICK,
              click: {
                name: t('gaTracking.projectViewTab.editDetails'),
                type: GA_CLICK_TYPES.BUTTON,
                region: GA_REGIONS.MODAL,
                action: `${t('gaTracking.global.edit')} - ${t(
                  'gaTracking.projectViewTab.projectProfile'
                )}`,
              },
              subPage: {
                subpage_name: t('gaTracking.projectViewTab.projectProfile'),
                subpage_type: GA_SUBPAGE_TYPE.MODAL,
              },
              hasProjectData: true,
            });
            setOnProjectEdit(true);
          }}
        />
      )}

      <AurButton
        classNames={`typo-text project-dialog-btn`}
        type="continue"
        size="Medium"
        label={t('label.close')}
        onClick={() => {
          trackGAEvent({
            eventName: GA_EVENT_NAMES.CLICK,
            click: {
              name: t('gaTracking.global.close'),
              type: GA_CLICK_TYPES.BUTTON,
              region: GA_REGIONS.MODAL,
              action: `${t('gaTracking.global.close')} - ${t(
                'gaTracking.projectViewTab.projectProfile'
              )}`,
            },
            subPage: {
              subpage_name: t('gaTracking.projectViewTab.projectProfile'),
              subpage_type: GA_SUBPAGE_TYPE.MODAL,
            },
            hasProjectData: true,
          });
          setOpenProfileDialog(false);
        }}
      />
    </>
  );

  return (
    <Box
      className={`project-overview-container ${projectDetails.workspaces.length > 0 ? 'has-workspace' : ''}`}
    >
      {isPermissionLoading ? (
        <Box className="permissions-loader">
          <AurLoadingIndicator show={isPermissionLoading} />
        </Box>
      ) : (
        <>
          <Box className="project-overview">
            {projectDetails.highlyConfidential ? (
              <Box className="project-overview-highly-confidential">
                <AurHighlyConfidential type="pill" />
              </Box>
            ) : (
              <></>
            )}
            <Box className="section-1">
              <Box className="overview-links">
                <AurButton
                  classNames="project-profile"
                  endIcon={<AurIcon icon={IconNames.ChevronRight} />}
                  type="text"
                  size="Medium"
                  label={t('welcomeScreenSingleClient.projectProfile')}
                  onClick={() => {
                    trackGAEvent({
                      eventName: GA_EVENT_NAMES.CLICK,
                      click: {
                        name: t('gaTracking.projectViewTab.productProfile'),
                        type: GA_CLICK_TYPES.BUTTON,
                        region: GA_REGIONS.BODY,
                        action: t(
                          'gaTracking.projectViewTab.openProductProfile'
                        ),
                      },
                      subPage: {
                        subpage_name: t(
                          'gaTracking.projectViewTab.projectView'
                        ),
                        subpage_type: GA_SUBPAGE_TYPE.ACCORDION,
                      },
                      hasProjectData: true,
                    });
                    setOpenProfileDialog(true);
                    setOnProjectEdit(false);
                  }}
                />
                {quickLinkList.length > 0 && (
                  <Box className="quick-link-container">
                    <Button
                      aria-controls={open ? 'dropdown-menu' : undefined}
                      aria-haspopup="true"
                      aria-expanded={open ? 'true' : undefined}
                      data-testid="quick-links-meny-btn"
                      onClick={onClickMenuButton}
                      disabled={isProjectClosed}
                      endIcon={
                        <AurIcon
                          icon={
                            !open ? IconNames.CaretDown : IconNames.CaretUp
                          }
                          color={IconColors.Custom}
                        />
                      }
                      className={`typo-text buttonMedium_size button-exit menu-btn ${open ? 'open' : ''}`}
                      variant="contained"
                    >
                      <Typography>
                        {t('welcomeScreenSingleClient.quickLinks')}
                      </Typography>
                    </Button>
                    <AurMenu
                      anchorElement={anchorEl}
                      shouldShow={open}
                      onClose={handleClose}
                      menuItems={quickLinkList}
                      direction="right"
                      aria-labelledby="addUserButton"
                    />
                  </Box>
                )}
              </Box>
              <Grid container className="project-overview-fields">
                <Grid item xs={3} className="field-groups">
                  <Box className="field-group">
                    <Typography className="typo-text label2_size" tabIndex={0}>
                      {t('welcomeScreenSingleClient.dataVolumeinReview')}
                    </Typography>
                    <Typography
                      className="typo-text display2_size"
                      tabIndex={0}
                    >
                      {totalDataManagementChartData
                        .reduce((total: number, item) => total + item.value, 0)
                        .toFixed(1) || 0}
                      &nbsp;
                      {t('reports.hostingVolume.cumulativeReport.gb')}
                    </Typography>
                    {totalDataManagementChartData.length > 0 && <AurButton
                      classNames="total-data-managed"
                      type="text"
                      size="Medium"
                      label={t(
                        'welcomeScreenSingleClient.clickDataVolumeButton'
                      )}

                      onClick={() => {
                        setTotalDataManagementdataDialogIsOpen(true);
                      }}
                    />}

                  </Box>
                </Grid>
                <Grid item xs={3} className="field-groups">
                  <Box className="field-group">
                    <Typography className="typo-text label2_size" tabIndex={0}>
                      {t('welcomeScreenSingleClient.branch')}
                    </Typography>
                    <Typography
                      className="typo-text subhead3_size"
                      tabIndex={0}
                    >
                      {projectDetails?.clientBranch?.name || '--'}
                    </Typography>
                  </Box>
                  <Box className="field-group">
                    <Typography className="typo-text label2_size" tabIndex={0}>
                      {t('welcomeScreenSingleClient.department')}
                    </Typography>
                    <Typography
                      className="typo-text subhead3_size"
                      tabIndex={0}
                    >
                      {projectDetails?.clientDepartment?.name || '--'}
                    </Typography>
                  </Box>
                  <Box className="field-group">
                    <Typography className="typo-text label2_size" tabIndex={0}>
                      {t('welcomeScreenSingleClient.InternalProjectName')}
                    </Typography>
                    <Typography
                      className="typo-text subhead3_size project-name-value"
                      tabIndex={0}
                    >
                      {internalProjectName || '--'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={3} className="field-groups">
                  <Box className="field-group">
                    <Typography className="typo-text label2_size" tabIndex={0}>
                      {t('welcomeScreenSingleClient.alphaCode')}
                    </Typography>
                    <Typography className="typo-text body1_size" tabIndex={0}>
                      {projectDetails.projectAlphaCode || '--'}
                    </Typography>
                  </Box>
                  <Box className="field-group">
                    <Typography className="typo-text label2_size" tabIndex={0}>
                      {t('welcomeScreenSingleClient.engagementCode')}
                    </Typography>
                    <Typography className="typo-text body1_size" tabIndex={0}>
                      {projectDetails.projectCode || '--'}
                    </Typography>
                  </Box>
                  <Box className="field-group">
                    <Typography className="typo-text label2_size" tabIndex={0}>
                      {t('welcomeScreenSingleClient.clientPreferredId')}
                    </Typography>
                    <Typography className="typo-text body1_size" tabIndex={0}>
                      {projectDetails.clientPreferredId || '--'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={3} className="field-groups">
                  <Box className="field-group">
                    <Typography className="typo-text label2_size" tabIndex={0}>
                      {t('common.hostCountry')}
                    </Typography>
                    <Typography className="typo-text body1_size" tabIndex={0}>
                      {projectDetails?.countryName ? (
                        <>
                          <AurCountryFlag
                            countryCode={
                              projectDetails?.domainCountryCode ?? ''
                            }
                            altText=""
                          />
                          <Box component="span" className="margin-left-5">
                            {projectDetails?.countryName}
                          </Box>
                        </>
                      ) : (
                        '--'
                      )}
                    </Typography>
                  </Box>
                  <Box className="field-group">
                    <Typography
                      className="typo-text label2_size mist-700"
                      tabIndex={0}
                    >
                      {t('common.dataCenterInstance')}
                    </Typography>
                    <Typography className="typo-text body1_size" tabIndex={0}>
                      {projectDetails.dataCenterName || '--'}
                    </Typography>
                  </Box>
                  <Box className="field-group">
                    <Typography
                      className="typo-text label2_size mist-700"
                      tabIndex={0}
                    >
                      {t('common.projectServiceType')}
                    </Typography>
                    <Typography className="typo-text body1_size" tabIndex={0}>
                      {projectDetails?.auroraType || t('common.notSpecified')}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
            <Box className="ECA-updates">
              <AurLoadingIndicator show={isLoading} />
              <Box className="ECA-update-1">
                {!projectDetails?.isNonConsilioWorkflow && (
                  <ConsilioWorkflowMetrics
                    projectDetails={projectDetails}
                    reportMetrics={reportMetrics}
                    onDataDeliveriesClick={loadIntakeAuditReport}
                    onDatasetsProcessingClick={loadProcessingInProgressReport}
                    onProcessedDatasetsClick={loadSummaryAndExceptionsReport}
                    onTotalVolumesMappedClick={
                      navigateToCompletedMappedDataReport
                    }
                  />
                )}
                {projectDetails?.isNonConsilioWorkflow && (
                  <NonConsilioWorkflowMetrics
                    projectDetails={projectDetails}
                    reportMetrics={reportMetrics}
                    onTotalVolumesMappedClick={
                      navigateToCompletedMappedDataReport
                    }
                  />
                )}
              </Box>
              <AurDialog
                showTitleInline
                dialogTitle={t(
                  'welcomeScreenSingleClient.dataVolumeinReviewBreakdown'
                )}
                // eslint-disable-next-line @typescript-eslint/no-empty-function
                onOk={() => { }}
                onCancel={() => {
                  setTotalDataManagementdataDialogIsOpen(false);
                }}
                showDialog={totalDataManagementdataDialogIsOpen}
                showActionButtons={false}
                popupContent={
                  totalDataManagementChartData.length > 0 ? (
                    <AurPieChart
                      data={totalDataManagementChartData}
                      size={{ height: 400, width: 650 }}
                      radius={138}
                      innerRadius={80}
                      labelPadding={20}
                      centerText={
                        <>
                          <tspan>
                            {totalDataManagementChartData
                              .reduce(
                                (total: number, item) => total + item.value,
                                0
                              )
                              .toFixed(1)}{' '}
                          </tspan>
                          <tspan
                            className="aur-pie-chart-center-sublabel"
                            dy={'4px'}
                          >
                            {' '}
                            {t('reports.hostingVolume.cumulativeReport.gb')}
                          </tspan>
                        </>
                      }
                    />
                  ) : (
                    <></>
                  )
                }
              />
              {projectDetails.ecaEnabled && (
                <Box className="ECA-update-2">
                  <Typography className="typo-text subhead1_size" tabIndex={0}>
                    {t('welcomeScreenSingleClient.ecaOverview')}
                  </Typography>
                  <Box className="overview">
                    <Box className="overview-set1">
                      <Typography
                        className="typo-text label2_size mute-text"
                        tabIndex={0}
                      >
                        {t('welcomeScreenSingleClient.totalDocuments')}
                      </Typography>
                      <Box className="document-count" gap="8px">
                        <AurSvgIcon src="images/eca-documents.svg" />
                        <Typography
                          className="typo-text display2_size"
                          tabIndex={0}
                        >
                          {formatNumberWithComma(
                            reportMetrics?.totalDocumentsCount
                          )}
                        </Typography>
                      </Box>
                    </Box>
                    <Box className="overview-set2">
                      <Typography
                        className="typo-text label2_size mute-text"
                        tabIndex={0}
                      >
                        {t('welcomeScreenSingleClient.exportedtoReview')}
                      </Typography>
                      <Box display="flex" alignItems="center">
                        <Box className="document-count" gap="8px">
                          <AurSvgIcon src="images/eca-document-search.svg" />
                          <Typography
                            className="typo-text display2_size"
                            tabIndex={0}
                          >
                            {formatNumberWithComma(
                              reportMetrics?.exportedDocumentCount
                            )}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography
                            className="typo-text body4_size mute-text"
                            tabIndex={0}
                          >
                            {t('welcomeScreenSingleClient.latestLoadDate')}
                          </Typography>
                          <AurLightTooltip
                            title={
                              formattedDate ? t('common.yearMonthDate') : ''
                            }
                          >
                            <span className="typo-text body4_size mute-text">
                              {formattedDate ?? ''}
                            </span>
                          </AurLightTooltip>
                        </Box>
                      </Box>
                    </Box>
                    <Box className="overview-set3">
                      {canViewEca && (
                        <AurButton
                          classNames="launch-eca"
                          endIcon={
                            <AurIcon
                              icon={IconNames.ChevronRight}
                              color={IconColors.Custom}
                              customClassName="color-white"
                            />
                          }
                          type="continue"
                          size="Medium"
                          disabled={isProjectClosed}
                          label={t('welcomeScreenSingleClient.launchECA')}
                          dataTestId="launch-eca-btn"
                          onClick={loadEcaPage}
                        />
                      )}
                    </Box>
                  </Box>
                </Box>
              )}
              {budgetReport && budgetDetails != null && (
                <Box className="project-budget-container">
                  <AurDonutchart
                    percentageValue={
                      budgetDetails?.percentageBudgetConsumed as number
                    }
                    backgroundColor="#121a25"
                    width={150}
                    height={150}
                    customClassDonutContainer="budget-donut-container"
                  />
                  <Box className="project-budget-details">
                    <Typography
                      className="typo-text subhead2_size"
                      tabIndex={0}
                    >
                      {t(
                        'welcomeScreenSingleClient.budgetTrackerDetails.budgetConsumption'
                      )}
                    </Typography>
                    <Typography
                      className="typo-text body2_size budget-description"
                      tabIndex={0}
                    >
                      {(budgetDetails?.percentageBudgetConsumed as number) *
                        BUDGET_CONSUMPTON_PERCENTAGE <
                        BUDGET_PERCENTAGE_LIMIT
                        ? `${t('welcomeScreenSingleClient.budgetTrackerDetails.budgetBelowDescription')}  ${formatCurrencyAmount(budgetDetails.currency, formatNumberWithComma(budgetDetails?.budgetLimit))} ${t('welcomeScreenSingleClient.budgetTrackerDetails.budgetLimit')}`
                        : `${t('welcomeScreenSingleClient.budgetTrackerDetails.budgetNearingDescription')} ${formatCurrencyAmount(budgetDetails.currency, formatNumberWithComma(budgetDetails?.budgetLimit))} ${t('welcomeScreenSingleClient.budgetTrackerDetails.limit')}`}
                    </Typography>
                    <AurButton
                      classNames="budget-view-details-btn"
                      type="continue"
                      size="Medium"
                      aria-describedby="view-button"
                      label={t('conciergeReports.buttonsLabels.viewDetails')}
                      dataTestId="launch-eca-btn"
                      onClick={loadBudgetDetails}
                    >
                      <Typography
                        id="view-button"
                        style={visuallyHidden}
                        tabIndex={0}
                      >
                        {t('label.viewEstimatedBudget')}
                      </Typography>
                    </AurButton>
                  </Box>
                </Box>
              )}
              <Box
                className="note"
                display="flex"
                justifyContent="space-between"
              >
                <Box>
                  <Typography className="typo-text body4_size">
                    {t('welcomeScreenSingleClient.policy')}
                    <AurLightTooltip
                      title={
                        formattedDate || metricsUpdatedOn
                          ? t('common.yearMonthDate')
                          : ''
                      }
                    >
                      <span>
                        {formattedDate
                          ? formattedDate
                          : getUTCDateTimeForDisplay(
                            metricsUpdatedOn ?? new Date(),
                            ONLY_DATE_SHOW_FORMAT
                          )}
                      </span>
                    </AurLightTooltip>
                  </Typography>
                  <Typography className="typo-text body4_size">
                    {t('welcomeScreenSingleClient.indText3')}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box className="welcome-alert">
              {sortedGroupedAlerts?.length > 0 ? (
                <div>
                  <span id="hosting-user-summary" className="addUserHint">
                    {t('reports.hostingUserSummary.alertHints')}
                  </span>
                  <div
                    data-testid="alert"
                    aria-describedby={'hosting-user-summary'}
                    aria-roledescription="carousel"
                    aria-live="polite"
                  >
                    <AurAlertCarousel
                      titleAlignment={'left'}
                      alertsData={
                        sortedGroupedAlerts?.map((alert: any) => ({
                          ...alert,
                          deleteButtonHandler: () =>
                            markAlertAsViewed(alert.userAlertId),
                          prevButtonHandler: () =>
                            (alert?.data ?? []).length > 1
                              ? datasetAlertsDialogRef.current?.openDialog(
                                alert
                              )
                              : onPressContinueFromAlertsDialog(
                                alert?.data?.[0]
                              ),
                          onPressAlertHandler: () =>
                            (alert?.data ?? []).length > 1
                              ? datasetAlertsDialogRef.current?.openDialog(
                                alert
                              )
                              : onPressContinueFromAlertsDialog(
                                alert?.data?.[0]
                              ),
                          showArrowCTA: true,
                          showDatasets: alert?.datasets?.length > 0,
                        })) as []
                      }
                    />
                  </div>
                </div>
              ) : null}
            </Box>
            <Box className="workspace-over-view">
              <Typography
                className="typo-text subhead1_size title"
                tabIndex={0}
              >
                {t('welcomeScreenSingleClient.workspaces')}
              </Typography>
              <Box>
                {projectDetails.workspaces.map((workspace, index: number) => (
                  <WorkspaceOverView
                    workSpaceLevel={index}
                    key={workspace.workspaceId}
                    workspaceDetails={workspace}
                    projectId={
                      projectDetails ? projectDetails.projectId : undefined
                    }
                    projectName={projectDetails.projectName}
                    projectCode={projectDetails.projectCode}
                    onExpand={() =>
                      setWorkspacesExpanded(workspacesExpanded + 1)
                    }
                    onCollapse={() =>
                      setWorkspacesExpanded(workspacesExpanded - 1)
                    }
                    ecaEnabled={projectDetails.ecaEnabled}
                    alerts={workspaceAlerts(workspace.workspaceId)}
                    onDeleteAlert={markAlertAsViewed}
                    hostingUsers={reportMetrics?.hostingUsers}
                    productionsCompleted={reportMetrics?.productionsCompleted}
                    hostingUserSummaryDate={
                      reportMetrics?.hostingUserSummaryDate
                    }
                  />
                ))}
              </Box>
            </Box>
          </Box>
          <DatasetAlertsDialog
            ref={datasetAlertsDialogRef}
            onPressContinue={onPressContinueFromAlertsDialog}
          />
          <ProjectProfileDialog
            headerTitle={
              onProjectEdit
                ? t('clientDetailsPage.projectDrawer.editProjectProfile')
                : t('clientDetailsPage.projectDrawer.projectProfile')
            }
            dialogContent={
              onProjectEdit ? (
                <ProjectProfileEditableDetails
                  projectDescription={projectDescription}
                  setProjectDescription={setProjectDescription}
                  overseeingAttorneys={overseeingAttorneys}
                  setOverseeingAttorneys={setOverseeingAttorneys}
                  entityName={entityName}
                  setEntityName={setEntityName}
                  clientUniqueProjectId={clientUniqueProjectId}
                  setClientUniqueProjectId={setClientUniqueProjectId}
                  overseeingAttorneysEmail={overseeingAttorneysEmail}
                  setOverseeingAttorneysEmail={setOverseeingAttorneysEmail}
                  additionalContactInfo={additionalContactInfo}
                  setAdditionalContactInfo={setAdditionalContactInfo}
                  internalProjectName={internalProjectName}
                  setInternalProjectName={setInternalProjectName}
                  hasError={(e) => setHasError(e)}
                />
              ) : (
                <ProjectProfileViewDetails
                  projectDescription={projectDescription}
                  overseeingAttorneys={overseeingAttorneys}
                  entityName={entityName}
                  clientUniqueProjectId={clientUniqueProjectId || '--'}
                  overseeingAttorneysEmail={overseeingAttorneysEmail}
                  additionalContactInfo={additionalContactInfo}
                  internalProjectName={internalProjectName || '--'}
                />
              )
            }
            dialogActions={onProjectEdit ? editActions : viewActions}
            open={openProfileDialog}
            onClose={() => {
              setOpenProfileDialog(false);
            }}
            onSave={handleSave}
          />

          <AurPopup
            open={showLaunchpointPopup}
            onOk={() => setShowLaunchpointPopup(false)}
            className="aur-unsaved-changes-popup location_mismatch_popup"
            showOkOption={true}
            showCancelOption={false}
            onCancel={() => {
              setShowLaunchpointPopup(false);
            }}
            buttonStyle={POPUP_BUTTON_STYLE.SIDEBYSIDE}
            showPopupHeading={false}
            popupContent={t(
              'welcomeScreenSingleClient.locationMismatchPopupContent'
            )}
            labelActionOk={t(
              'welcomeScreenSingleClient.locationMismatchPopupOkButton'
            )}
            showPopupTitle={true}
            showCloseIcon={true}
          />
          <AurPopup
            open={showUploadEvidencePopup}
            onOk={() => setShowUploadEvidencePopup(false)}
            className="aur-unsaved-changes-popup"
            showCancelOption={false}
            onCancel={() => {
              setShowUploadEvidencePopup(false);
            }}
            showPopupHeading={true}
            buttonStyle={POPUP_BUTTON_STYLE.STACKED}
            popupHeading={t('welcomeScreenSingleClient.uploadEvidenceTitle')}
            popupContent={t('welcomeScreenSingleClient.uploadEvidenceContent')}
            labelActionOk={t(
              'welcomeScreenSingleClient.locationMismatchPopupOkButton'
            )}
          />
        </>
      )}
    </Box>
  );
};

export default ProjectOverView;
